import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, Play, Pause, UserMinus, Mail, MessageSquare,
  Filter, Search, ChevronDown, Clock, CheckCircle, XCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Enrollments() {
  const [enrollments, setEnrollments] = useState([]);
  const [sequences, setSequences] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSequence, setSelectedSequence] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showEnrollModal, setShowEnrollModal] = useState(false);

  const token = localStorage.getItem('auth_token');

  useEffect(() => {
    fetchData();
  }, [selectedSequence, statusFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      
      // Build query params
      let queryParams = new URLSearchParams();
      if (selectedSequence) queryParams.append('sequence_id', selectedSequence);
      if (statusFilter) queryParams.append('status', statusFilter);

      const [enrollmentsRes, sequencesRes, leadsRes] = await Promise.all([
        fetch(`${API_URL}/api/nurturing/enrollments?${queryParams}`, { headers }),
        fetch(`${API_URL}/api/nurturing/sequences`, { headers }),
        fetch(`${API_URL}/api/leads`, { headers })
      ]);

      if (enrollmentsRes.ok) {
        const data = await enrollmentsRes.json();
        setEnrollments(data.enrollments || []);
      }

      if (sequencesRes.ok) {
        const data = await sequencesRes.json();
        setSequences(data.sequences || []);
      }

      if (leadsRes.ok) {
        const data = await leadsRes.json();
        setLeads(data.leads || []);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load enrollments');
    } finally {
      setLoading(false);
    }
  };

  const enrollLeads = async (leadIds, sequenceId) => {
    try {
      const res = await fetch(`${API_URL}/api/nurturing/enroll`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ lead_ids: leadIds, sequence_id: sequenceId })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(`Enrolled ${data.enrolled} leads, skipped ${data.skipped}`);
        fetchData();
        setShowEnrollModal(false);
      } else {
        toast.error('Failed to enroll leads');
      }
    } catch (error) {
      toast.error('Failed to enroll leads');
    }
  };

  const pauseEnrollment = async (enrollmentId) => {
    try {
      const res = await fetch(`${API_URL}/api/nurturing/enrollments/${enrollmentId}/pause`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Enrollment paused');
        fetchData();
      } else {
        toast.error('Failed to pause enrollment');
      }
    } catch (error) {
      toast.error('Failed to pause enrollment');
    }
  };

  const resumeEnrollment = async (enrollmentId) => {
    try {
      const res = await fetch(`${API_URL}/api/nurturing/enrollments/${enrollmentId}/resume`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Enrollment resumed');
        fetchData();
      } else {
        toast.error('Failed to resume enrollment');
      }
    } catch (error) {
      toast.error('Failed to resume enrollment');
    }
  };

  const unenrollLead = async (enrollmentId) => {
    if (!window.confirm('Remove this lead from the sequence?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/nurturing/enrollments/${enrollmentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Lead unenrolled');
        fetchData();
      } else {
        toast.error('Failed to unenroll lead');
      }
    } catch (error) {
      toast.error('Failed to unenroll lead');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      paused: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      completed: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      unsubscribed: 'bg-red-500/20 text-red-400 border-red-500/30'
    };
    return styles[status] || 'bg-slate-500/20 text-emerald-300/70 border-slate-500/30';
  };

  const getSequenceName = (sequenceId) => {
    const seq = sequences.find(s => s.id === sequenceId);
    return seq?.name || 'Unknown Sequence';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">Enrollments</h1>
        <p className="text-emerald-300/70">Manage leads enrolled in nurturing sequences</p>
      </div>

      {/* Actions & Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <Button 
          onClick={() => setShowEnrollModal(true)}
          className="bg-emerald-600 hover:bg-emerald-700"
          data-testid="enroll-leads-btn"
        >
          <Users className="w-4 h-4 mr-2" />
          Enroll Leads
        </Button>

        <div className="flex gap-2 flex-1">
          <select
            value={selectedSequence}
            onChange={(e) => setSelectedSequence(e.target.value)}
            className="px-4 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white min-w-[200px]"
            data-testid="sequence-filter"
          >
            <option value="">All Sequences</option>
            {sequences.map(seq => (
              <option key={seq.id} value={seq.id}>{seq.name}</option>
            ))}
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white"
            data-testid="status-filter"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="completed">Completed</option>
            <option value="unsubscribed">Unsubscribed</option>
          </select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-[#052e22]/50 border-emerald-800/30">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-500/20">
              <Users className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{enrollments.length}</p>
              <p className="text-sm text-emerald-300/70">Total Enrolled</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#052e22]/50 border-emerald-800/30">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-500/20">
              <Play className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">
                {enrollments.filter(e => e.status === 'active').length}
              </p>
              <p className="text-sm text-emerald-300/70">Active</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#052e22]/50 border-emerald-800/30">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/20">
              <CheckCircle className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">
                {enrollments.filter(e => e.status === 'completed').length}
              </p>
              <p className="text-sm text-emerald-300/70">Completed</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#052e22]/50 border-emerald-800/30">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-yellow-500/20">
              <Pause className="w-5 h-5 text-yellow-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">
                {enrollments.filter(e => e.status === 'paused').length}
              </p>
              <p className="text-sm text-emerald-300/70">Paused</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Enrollments List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500"></div>
        </div>
      ) : enrollments.length === 0 ? (
        <Card className="bg-[#052e22]/50 border-emerald-800/30 border-dashed">
          <CardContent className="p-12 text-center">
            <Users className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">No enrollments yet</h3>
            <p className="text-emerald-300/70 mb-4">Enroll leads in sequences to start nurturing them automatically</p>
            <Button 
              onClick={() => setShowEnrollModal(true)}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              Enroll Leads
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="bg-[#052e22]/50 border border-emerald-800/30 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-emerald-800/30">
                <th className="text-left py-3 px-4 text-sm font-medium text-emerald-300/70">Lead</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-emerald-300/70">Sequence</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-emerald-300/70">Progress</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-emerald-300/70">Status</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-emerald-300/70">Enrolled</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-emerald-300/70">Actions</th>
              </tr>
            </thead>
            <tbody>
              {enrollments.map((enrollment) => (
                <motion.tr 
                  key={enrollment.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="border-b border-emerald-800/30/50 hover:bg-[#0a3d2e]/30"
                  data-testid={`enrollment-row-${enrollment.id}`}
                >
                  <td className="py-3 px-4">
                    <div>
                      <p className="font-medium text-white">{enrollment.lead?.name || 'Unknown'}</p>
                      <p className="text-sm text-emerald-300/70">{enrollment.lead?.email}</p>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <p className="text-slate-300">{getSequenceName(enrollment.sequence_id)}</p>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <div className="w-20 h-2 bg-slate-700 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-emerald-500 rounded-full"
                          style={{ 
                            width: `${Math.min((enrollment.current_step / 6) * 100, 100)}%` 
                          }}
                        />
                      </div>
                      <span className="text-sm text-emerald-300/70">Step {enrollment.current_step}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <Badge className={`${getStatusBadge(enrollment.status)} border`}>
                      {enrollment.status}
                    </Badge>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-sm text-emerald-300/70">
                      {new Date(enrollment.enrolled_at).toLocaleDateString()}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center justify-end gap-1">
                      {enrollment.status === 'active' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-yellow-400 hover:text-yellow-300"
                          onClick={() => pauseEnrollment(enrollment.id)}
                          data-testid={`pause-enrollment-${enrollment.id}`}
                        >
                          <Pause className="w-4 h-4" />
                        </Button>
                      )}
                      {enrollment.status === 'paused' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-emerald-400 hover:text-emerald-300"
                          onClick={() => resumeEnrollment(enrollment.id)}
                          data-testid={`resume-enrollment-${enrollment.id}`}
                        >
                          <Play className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-400 hover:text-red-300"
                        onClick={() => unenrollLead(enrollment.id)}
                        data-testid={`unenroll-${enrollment.id}`}
                      >
                        <UserMinus className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Enroll Modal */}
      {showEnrollModal && (
        <EnrollModal
          sequences={sequences.filter(s => s.status === 'active' || s.total_steps > 0)}
          leads={leads}
          onClose={() => setShowEnrollModal(false)}
          onEnroll={enrollLeads}
        />
      )}
    </div>
  );
}

// Enroll Modal Component
function EnrollModal({ sequences, leads, onClose, onEnroll }) {
  const [selectedSequence, setSelectedSequence] = useState('');
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [filterTemp, setFilterTemp] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const filteredLeads = leads.filter(lead => {
    const matchesSearch = !searchTerm || 
      lead.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.company?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesTemp = !filterTemp || lead.temperature === filterTemp;
    
    return matchesSearch && matchesTemp;
  });

  const toggleLead = (leadId) => {
    setSelectedLeads(prev => 
      prev.includes(leadId) 
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  const selectAll = () => {
    setSelectedLeads(filteredLeads.map(l => l.id));
  };

  const deselectAll = () => {
    setSelectedLeads([]);
  };

  const handleEnroll = () => {
    if (!selectedSequence) {
      toast.error('Please select a sequence');
      return;
    }
    if (selectedLeads.length === 0) {
      toast.error('Please select at least one lead');
      return;
    }
    onEnroll(selectedLeads, selectedSequence);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-[#052e22] border border-emerald-800/30 rounded-xl w-full max-w-2xl max-h-[80vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-emerald-800/30">
          <h3 className="text-xl font-semibold text-white">Enroll Leads in Sequence</h3>
          <p className="text-sm text-emerald-300/70 mt-1">Select leads and a sequence to start nurturing</p>
        </div>

        <div className="p-6">
          {/* Enrollment Process Steps */}
          <div className="mb-6 bg-[#0a3d2e]/50 rounded-lg p-4 border border-emerald-800/30">
            <p className="text-sm text-emerald-300/90 font-medium mb-2">How Enrollment Works:</p>
            <div className="flex items-center gap-3 text-sm">
              <div className="flex items-center gap-2 text-emerald-400">
                <span className="w-6 h-6 rounded-full bg-emerald-600 flex items-center justify-center text-xs font-bold">1</span>
                <span>Select sequence</span>
              </div>
              <ChevronDown className="w-4 h-4 text-emerald-600 rotate-[-90deg]" />
              <div className="flex items-center gap-2 text-emerald-400">
                <span className="w-6 h-6 rounded-full bg-emerald-600 flex items-center justify-center text-xs font-bold">2</span>
                <span>Pick leads</span>
              </div>
              <ChevronDown className="w-4 h-4 text-emerald-600 rotate-[-90deg]" />
              <div className="flex items-center gap-2 text-emerald-400">
                <span className="w-6 h-6 rounded-full bg-emerald-600 flex items-center justify-center text-xs font-bold">3</span>
                <span>Auto-nurturing begins</span>
              </div>
            </div>
          </div>
          {/* Sequence Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-emerald-300/70 mb-2">Select Sequence</label>
            <select
              value={selectedSequence}
              onChange={(e) => setSelectedSequence(e.target.value)}
              className="w-full px-4 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white"
              data-testid="enroll-sequence-select"
            >
              <option value="">Choose a sequence...</option>
              {sequences.map(seq => (
                <option key={seq.id} value={seq.id}>
                  {seq.name} ({seq.total_steps || 0} steps)
                </option>
              ))}
            </select>
          </div>

          {/* Filters */}
          <div className="flex gap-2 mb-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input
                type="text"
                placeholder="Search leads..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white placeholder-slate-500"
                data-testid="lead-search-input"
              />
            </div>
            <select
              value={filterTemp}
              onChange={(e) => setFilterTemp(e.target.value)}
              className="px-4 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white"
              data-testid="temperature-filter"
            >
              <option value="">All Leads</option>
              <option value="hot">Hot Leads</option>
              <option value="warm">Warm Leads</option>
              <option value="cold">Cold Leads</option>
            </select>
          </div>

          {/* Selection Actions */}
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-emerald-300/70">
              {selectedLeads.length} of {filteredLeads.length} selected
            </span>
            <div className="flex gap-2">
              <button
                onClick={selectAll}
                className="text-sm text-emerald-400 hover:text-emerald-300"
              >
                Select All
              </button>
              <button
                onClick={deselectAll}
                className="text-sm text-emerald-300/70 hover:text-slate-300"
              >
                Deselect All
              </button>
            </div>
          </div>

          {/* Leads List */}
          <div className="max-h-[300px] overflow-y-auto border border-emerald-800/30 rounded-lg">
            {filteredLeads.length === 0 ? (
              <div className="p-8 text-center text-emerald-300/70">No leads found</div>
            ) : (
              filteredLeads.map(lead => (
                <label
                  key={lead.id}
                  className="flex items-center gap-3 p-3 hover:bg-[#0a3d2e]/50 cursor-pointer border-b border-emerald-800/30 last:border-0"
                >
                  <input
                    type="checkbox"
                    checked={selectedLeads.includes(lead.id)}
                    onChange={() => toggleLead(lead.id)}
                    className="rounded border-slate-600 bg-[#0a3d2e] text-emerald-600"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-white truncate">{lead.name}</p>
                    <p className="text-sm text-emerald-300/70 truncate">{lead.email}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {lead.temperature && (
                      <Badge className={`text-xs ${
                        lead.temperature === 'hot' ? 'bg-red-500/20 text-red-400' :
                        lead.temperature === 'warm' ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-blue-500/20 text-blue-400'
                      }`}>
                        {lead.temperature}
                      </Badge>
                    )}
                    <span className="text-sm text-gray-500">{lead.score}</span>
                  </div>
                </label>
              ))
            )}
          </div>
        </div>

        <div className="p-6 border-t border-emerald-800/30 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} className="text-emerald-300/70">
            Cancel
          </Button>
          <Button 
            onClick={handleEnroll}
            className="bg-emerald-600 hover:bg-emerald-700"
            disabled={!selectedSequence || selectedLeads.length === 0}
            data-testid="enroll-submit-btn"
          >
            Enroll {selectedLeads.length} Lead{selectedLeads.length !== 1 ? 's' : ''}
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
}
